import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bot, Key, Save, Trash2, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AISettingsCard = () => {
  const [cfg, setCfg] = useState(null);
  const [provider, setProvider] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ki-assistent/settings`).then((r) => {
      setCfg(r.data);
      setProvider(r.data.provider || "");
      setModel(r.data.model || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (!provider) {
      toast.error("Bitte wähle einen Provider (OpenAI oder Claude).");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ki-assistent/settings`, {
        provider, api_key: apiKey.trim(), model,
      });
      setCfg(r.data);
      setApiKey("");
      setTestResult(null);
      toast.success("KI-Assistent Einstellungen gespeichert!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ki-assistent/settings`, { remove_key: true });
      setCfg(r.data);
      setProvider("");
      setModel("");
      setTestResult(null);
      toast.success("KI-Konfiguration entfernt.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Entfernen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/ki-assistent/test`);
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || "Verbindungstest fehlgeschlagen" });
    } finally { setTesting(false); }
  };

  if (!cfg) return null;

  const models = cfg.available_models?.[provider] || [];

  return (
    <Card className="p-6 bg-white border-[var(--brand-border)]" data-testid="ai-settings-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[var(--brand-primary)] flex items-center justify-center shrink-0">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">KI-Assistent</h2>
          <p className="text-sm text-[var(--brand-muted)]">
            Verbinde deinen eigenen KI-Provider (OpenAI oder Claude) für den universellen Assistenten.
            Er hilft dir bei Angeboten, Rechnungen, Steuern, CRM und Reisen.
          </p>
        </div>
      </div>

      {/* Status */}
      {cfg.configured && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700 font-medium">
            Aktiv – {cfg.provider === "openai" ? "OpenAI" : "Claude (Anthropic)"} · {cfg.model}
          </span>
        </div>
      )}

      {/* Provider Selection */}
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium mb-2 block">KI-Provider wählen</Label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setProvider("openai"); setModel(cfg.available_models?.openai?.[0]?.id || "gpt-4o-mini"); }}
              className={`p-4 rounded-lg border-2 text-left transition ${
                provider === "openai"
                  ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]"
                  : "border-[var(--brand-border)] hover:border-[var(--brand-primary)]/50"
              }`}
              data-testid="provider-openai"
            >
              <div className="font-semibold text-sm text-[var(--brand-ink)]">OpenAI</div>
              <div className="text-xs text-[var(--brand-muted)] mt-1">ChatGPT (GPT-4o, GPT-4.1)</div>
            </button>
            <button
              onClick={() => { setProvider("anthropic"); setModel(cfg.available_models?.anthropic?.[0]?.id || "claude-sonnet-4-20250514"); }}
              className={`p-4 rounded-lg border-2 text-left transition ${
                provider === "anthropic"
                  ? "border-[var(--brand-primary)] bg-[var(--brand-bg)]"
                  : "border-[var(--brand-border)] hover:border-[var(--brand-primary)]/50"
              }`}
              data-testid="provider-anthropic"
            >
              <div className="font-semibold text-sm text-[var(--brand-ink)]">Anthropic</div>
              <div className="text-xs text-[var(--brand-muted)] mt-1">Claude (Sonnet 4, Haiku 3.5)</div>
            </button>
          </div>
        </div>

        {/* Model Selection */}
        {provider && models.length > 0 && (
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Modell</Label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full h-10 rounded-lg border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:border-[var(--brand-primary)]"
              data-testid="model-select"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            <p className="text-xs text-[var(--brand-muted)] mt-1">
              {models.find((m) => m.id === model)?.description || ""}
            </p>
          </div>
        )}

        {/* API Key */}
        {provider && (
          <div>
            <Label className="text-sm font-medium mb-1.5 block">
              {provider === "openai" ? "OpenAI API-Key" : "Anthropic API-Key"}
            </Label>
            <Input
              type="password"
              placeholder={cfg.has_key ? "•••••••• (gespeichert – zum Ändern neuen Key eingeben)" : provider === "openai" ? "sk-..." : "sk-ant-..."}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="border-[var(--brand-border)]"
              data-testid="ai-key-input"
            />
            <p className="text-xs text-[var(--brand-muted)] mt-1">
              {provider === "openai"
                ? "Erstelle deinen Key auf platform.openai.com → API Keys"
                : "Erstelle deinen Key auf console.anthropic.com → API Keys"}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={save} disabled={busy || (!apiKey.trim() && !cfg.has_key) || !provider}
            className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="ai-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {busy ? "Speichern…" : "Speichern"}
          </Button>
          {cfg.configured && (
            <Button onClick={testConnection} disabled={testing} variant="outline"
              className="border-[var(--brand-border)]" data-testid="ai-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Bot className="w-4 h-4 mr-1.5" />}
              {testing ? "Teste…" : "Verbindung testen"}
            </Button>
          )}
          {cfg.has_key && (
            <Button onClick={remove} disabled={busy} variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50" data-testid="ai-remove-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Entfernen
            </Button>
          )}
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`p-3 rounded-lg border ${testResult.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-center gap-2">
              {testResult.ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
              <span className={`text-sm font-medium ${testResult.ok ? "text-green-700" : "text-red-700"}`}>
                {testResult.ok ? "Verbindung erfolgreich!" : "Verbindung fehlgeschlagen"}
              </span>
            </div>
            {testResult.message && (
              <p className="text-xs mt-1 ml-6 text-[var(--brand-muted)]">{testResult.message}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
